import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { setExtractedData, setEditableData, setValidationErrors, clearValidationErrors, setDeclarationDirty, clearSelectedTemplate, setSelectedTemplate, updateTabValidationErrors, updateTabEditableData, updateTabIsDirty, updateWorkflowSessionState } from '../store/reducers/index';
import { selectActiveTabId } from '../store/selectors/tabSelectors';
import { useLanguage } from './useLanguage';
import { deepClone } from '../utils/dataHelpers';
import { removeFieldErrorImmediately, isEmpty, validateRequiredFieldsOnly } from '../utils/validationUtils';
import { getDeclarationKind } from '../utils/declarationKindUtils';
import { getFieldId } from '../utils/fieldIdUtils';
import { createEditableDataHash, createValidationErrorsHash } from '../utils/hashUtils';
import { hasValidationErrorsForExport, getPendingByExportExcludingParticipants } from '../utils/errorCheckUtils';
import { scroller } from 'react-scroll';

export const useDeclarationSummary = (jsonData) => {
  const dispatch = useDispatch();
  const store = useStore();
  const extractedData = useSelector(state => state.documents.extractedData);
  
  // Get active tab ID (for UI viewing only - TabBar)
  const activeTabId = useSelector(selectActiveTabId);
  
  // Get active workflow session ID (for storage)
  const activeWorkflowSessionId = useSelector(state => state.workflowSessions?.activeSessionId);
  const activeWorkflowSession = useSelector(state => 
    activeWorkflowSessionId ? state.workflowSessions?.sessions?.[activeWorkflowSessionId] : null
  );
  
  // Get editableData from active workflow session state (all declarations together, NOT split by tabs). Workflow sessions are source of truth
  const editableDataFromRedux = useSelector(state => {
    // Priority 1: Active workflow session's editableData (all declarations together), normalize to array
    if (activeWorkflowSessionId) {
      const workflowSession = state.workflowSessions?.sessions?.[activeWorkflowSessionId];
      if (workflowSession?.state?.editableData) {
        const workflowEditableData = workflowSession.state.editableData;
        return Array.isArray(workflowEditableData) 
          ? workflowEditableData 
          : (workflowEditableData && typeof workflowEditableData === 'object' ? [workflowEditableData] : []);
      }
    }
    // Priority 2: Fallback to global state (backward compatibility)
    return state.documents.editableData;
  });
  
  // Refs to track previous values for debugging (declare before use in selectors)
  const validationErrorsRef = useRef(null);
  const isDirtyRef = useRef(false);
  const hasLoggedIsDirtyRef = useRef(false);
  
  // Validation: scoped to active workflow when a session exists — never inherit global/tab errors from another workflow
  const validationErrors = useSelector(state => {
    const workflowSessionsState = state.workflowSessions;
    const currentActiveSessionId = workflowSessionsState?.activeSessionId;
    if (currentActiveSessionId) {
      const session = workflowSessionsState?.sessions?.[currentActiveSessionId];
      if (session?.state) {
        const workflowErrors = session.state.validationErrors;
        const resolved = Array.isArray(workflowErrors) ? workflowErrors : [];
        validationErrorsRef.current = resolved;
        return resolved;
      }
    }

    const tabsState = state.tabs;
    const currentActiveTabId = tabsState?.activeTabId;
    if (currentActiveTabId && tabsState.tabs?.[currentActiveTabId]) {
      const tabErrors = tabsState.tabs[currentActiveTabId].validationErrors;
      const resolved = Array.isArray(tabErrors) ? tabErrors : [];
      validationErrorsRef.current = resolved;
      return resolved;
    }

    const globalErrors = state.validationErrors.errors;
    validationErrorsRef.current = globalErrors;
    return globalErrors;
  });
  
  const isSpanishFromRedux = useSelector(state => state.validationErrors.isSpanish);
  
  // Get isDirty from active tab or global state (ensures detection when switching between tabs and "All" view)
  const isDirty = useSelector(state => {
    const currentActiveTabId = state.tabs?.activeTabId;
    const globalIsDirty = state.documents.isDirty || false;
    
    // If viewing "All" (no activeTabId), use global isDirty
    if (!currentActiveTabId) {
      if (isDirtyRef.current !== globalIsDirty) {
        isDirtyRef.current = globalIsDirty;
      }
      return globalIsDirty;
    }
    
    // Check both tab and global isDirty to handle edge cases where one might be set but not the other
    if (state.tabs?.tabs?.[currentActiveTabId]) {
      const tabIsDirty = state.tabs.tabs[currentActiveTabId].isDirty || false;
      const finalIsDirty = tabIsDirty || globalIsDirty;
      
      if (isDirtyRef.current !== finalIsDirty) {
        isDirtyRef.current = finalIsDirty;
      }
      return finalIsDirty;
    }
    
    // Fallback to global if tab doesn't exist
    if (isDirtyRef.current !== globalIsDirty) {
      isDirtyRef.current = globalIsDirty;
    }
    return globalIsDirty;
  });

  // Initialize isSpanish from cookie, default to true if not set
  const [isSpanish, setIsSpanish] = useLanguage();
  const [localEditableData, setLocalEditableData] = useState([]);
  
  // Store original data for cancel functionality
  const originalDataRef = useRef(null);
  const isFirstMount = useRef(true);
  const fechaCreacionInitializedRef = useRef(false); // Track if fecha_creacion has been initialized
  
  // Use refs to avoid dependencies in callbacks (performance optimization) Normalize to array for ref as well
  const rawEditableDataForRef = editableDataFromRedux || localEditableData;
  const normalizedEditableDataForRef = Array.isArray(rawEditableDataForRef) 
    ? rawEditableDataForRef 
    : (rawEditableDataForRef && typeof rawEditableDataForRef === 'object' ? [rawEditableDataForRef] : []);
  const editableDataRef = useRef(normalizedEditableDataForRef);
  
  // Track previous workflow session ID to detect switches
  const prevWorkflowSessionIdRef = useRef(activeWorkflowSessionId);
  
  // OPTIMIZED: Centralized validation clearing helper
  const clearValidationErrorsHelper = useCallback(() => {
    if (activeTabId) {
      dispatch(updateTabValidationErrors(activeTabId, []));
    } else {
      dispatch(clearValidationErrors());
    }
    if (activeWorkflowSessionId) {
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { validationErrors: [] }));
    }
  }, [activeTabId, activeWorkflowSessionId, dispatch]);
  
  // Sync editableDataRef when Redux data changes (never touch originalDataRef - managed only by initial load, handleChange, saveChanges)
  useEffect(() => {
    const workflowSwitched = prevWorkflowSessionIdRef.current !== activeWorkflowSessionId;
    if (workflowSwitched) {
      prevWorkflowSessionIdRef.current = activeWorkflowSessionId;
      // Don't clear errors on workflow switch - they should persist per workflow
      // Validation will be recalculated when data loads
    }
    
    const rawData = editableDataFromRedux || localEditableData;
    const normalizedData = Array.isArray(rawData) ? rawData : (rawData && typeof rawData === 'object' ? [rawData] : []);
    
    const currentData = editableDataRef.current;
    
    // When dirty, only sync if data structure changed or workflow switched (don't overwrite unsaved changes)
    if (isDirtySetRef.current) {
      if (workflowSwitched) {
        // Workflow switched - always sync
        try {
          const freshCopy = deepClone(normalizedData);
          editableDataRef.current = freshCopy;
          if (normalizedData.length > 0 && !originalDataRef.current) {
            originalDataRef.current = deepClone(freshCopy);
          }
        } catch (e) {
          editableDataRef.current = normalizedData;
        }
        return;
      }
      
      // Check if Redux has structural changes (e.g., from DocumentsTableContent or PackagesTableContent updates)
      if (!currentData || currentData.length !== normalizedData.length) {
        try {
          const freshCopy = deepClone(normalizedData);
          editableDataRef.current = freshCopy;
        } catch (e) {
          editableDataRef.current = normalizedData;
        }
        return;
      }
      
      // Same structure, dirty - don't overwrite ref (will sync on save or debounced update)
      return;
    }
    
    // Not dirty - safe to sync normally (fast comparison: length, reference, then hash comparison)
    const currentHash = createEditableDataHash(currentData);
    const normalizedHash = createEditableDataHash(normalizedData);
    const dataChanged = !currentData || 
      currentData.length !== normalizedData.length || 
      (normalizedData.length > 0 && currentData !== normalizedData && 
       currentHash !== normalizedHash);
    
    if (!dataChanged) return;
    
    try {
      const freshCopy = deepClone(normalizedData);
      editableDataRef.current = freshCopy;
      
      // Set originalDataRef only on initial load
      if (normalizedData.length > 0 && !originalDataRef.current) {
        originalDataRef.current = deepClone(freshCopy);
      }
      
      // Clear validation errors if data becomes empty (but not on workflow switch - already cleared above)
      if (normalizedData.length === 0 && currentData?.length > 0 && !workflowSwitched) {
        clearValidationErrorsHelper();
      }
      
      // Trigger validation only if we have data
      if (triggerValidationRef.current && freshCopy.length > 0) {
        setTimeout(() => triggerValidationRef.current(), 100);
      }
    } catch (e) {
      // Fallback: direct assignment
      editableDataRef.current = normalizedData;
      if (normalizedData.length > 0 && !originalDataRef.current) {
        originalDataRef.current = deepClone(normalizedData);
      }
      if (normalizedData.length === 0 && currentData?.length > 0 && !workflowSwitched) {
        clearValidationErrorsHelper();
      }
    }
  }, [editableDataFromRedux, localEditableData, activeWorkflowSessionId, clearValidationErrorsHelper]);
  
  // Update validationErrorsRef when validationErrors changes (for use in callbacks)
  // CRITICAL: Also sync from workflow session state to ensure ref is always up-to-date
  useEffect(() => {
    validationErrorsRef.current = validationErrors;
    
    // Also check workflow session state directly to ensure ref is synced
    const currentState = store.getState();
    const currentActiveWorkflowSessionId = currentState?.workflowSessions?.activeSessionId;
    if (currentActiveWorkflowSessionId) {
      const workflowSession = currentState?.workflowSessions?.sessions?.[currentActiveWorkflowSessionId];
      const workflowErrors = workflowSession?.state?.validationErrors;
      if (workflowErrors && Array.isArray(workflowErrors) && workflowErrors.length > 0) {
        validationErrorsRef.current = workflowErrors;
      }
    }
  }, [validationErrors, store]);
  
  // Use Redux editableData if available, otherwise use local state Normalize to array: if it's a single object, wrap it in an array
  const rawEditableData = editableDataFromRedux || localEditableData;
  const editableData = Array.isArray(rawEditableData) 
    ? rawEditableData 
    : (rawEditableData && typeof rawEditableData === 'object' ? [rawEditableData] : []);
  
  // Removed debug logging to prevent console spam

  // NOTE: originalExtractedData is deprecated - data is stored in workflow tabs This effect is kept for backward compatibility but doesn't use originalExtractedData
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      // Data is in tabs, not originalExtractedData
      return;
    }

    // Data is stored in tabs, not originalExtractedData Tabs are the source of truth for workflow data
    if (extractedData) {
      // Data is in tabs, no need to store in originalExtractedData
    } else {
      // Data is in tabs, no need to clear originalExtractedData
      const lastJobId = sessionStorage.getItem('lastJobId');
      // Data is in tabs, not originalExtractedData
    }
  }, [extractedData]);

  useEffect(() => {
    // CRITICAL: Early return if editableDataFromRedux is already empty and there's no data to process This prevents infinite loops when workflow session state is already set to empty array
    const isReduxEmpty = Array.isArray(editableDataFromRedux) && editableDataFromRedux.length === 0;
    const hasNoSourceData = !extractedData && !jsonData;
    if (isReduxEmpty && hasNoSourceData) {
      // Already empty and no source data, nothing to do
      return;
    }
    
    // Only initialize if we don't have editableData in Redux
    if (editableDataFromRedux) {
      // Validate editableDataFromRedux structure
      const isValidData = Array.isArray(editableDataFromRedux) 
        ? editableDataFromRedux.length > 0 && editableDataFromRedux.every(d => d && typeof d === 'object')
        : editableDataFromRedux && typeof editableDataFromRedux === 'object' && Object.keys(editableDataFromRedux).length > 0;
      
      if (isValidData) {
        // Only replace fecha_creacion on first load, not on subsequent renders
        if (!fechaCreacionInitializedRef.current) {
          // Helper function to get current timestamp
          const getCurrentTimestamp = () => {
            const now = new Date();
            return now.toISOString().slice(0, 19); // YYYY-MM-DDTHH:MM:SS
          };
          
          // Replace fecha_creacion with current time on first load This ensures fecha_creacion is always set to current time when data is first loaded
          const dataWithCurrentTime = Array.isArray(editableDataFromRedux)
            ? editableDataFromRedux.map(declaration => ({
                ...declaration,
                fecha_creacion: getCurrentTimestamp()
              }))
            : {
                ...editableDataFromRedux,
                fecha_creacion: getCurrentTimestamp()
              };
          
          // Update Redux with fecha_creacion replaced Store in workflow session state (all declarations together, not split by tabs)
          if (activeWorkflowSessionId) {
            dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
              editableData: dataWithCurrentTime,
            }));
          }
          dispatch(setEditableData(dataWithCurrentTime)); // Keep global for backward compatibility
          editableDataRef.current = dataWithCurrentTime;
          fechaCreacionInitializedRef.current = true;
          
          // CRITICAL: Store original data if not already stored (initial load only) This is the baseline state before any edits
          if (!originalDataRef.current) {
            originalDataRef.current = deepClone(dataWithCurrentTime);
          }
          return; // Use existing Redux data (now with fecha_creacion updated)
        }
        
        // CRITICAL: Store original data if not already stored (initial load only) This is the baseline state before any edits
        if (!originalDataRef.current) {
          originalDataRef.current = deepClone(editableDataFromRedux);
        }
        return; // Use existing Redux data
      } else {
        console.warn('editableDataFromRedux is invalid, falling back to extractedData/jsonData');
      }
    }
    
    // Try extractedData first, then jsonData, then sessionStorage as fallback
    let dataToUse = extractedData || jsonData;
    
    // Fallback: Data should come from tabs, not originalExtractedData (deprecated) If dataToUse is null/empty, it means tabs haven't been created yet In that case, we'll create tabs from extractedData in the migration effect
    
    // Final validation: ensure dataToUse is valid before processing
    if (!dataToUse || (Array.isArray(dataToUse) && dataToUse.length === 0) || (typeof dataToUse === 'object' && Object.keys(dataToUse).length === 0)) {
      // CRITICAL: Only update if both editableDataFromRedux and localEditableData are not already empty (prevent infinite loop) If they're already [], we've already set them, so skip to prevent infinite loops
      const isReduxAlreadyEmpty = Array.isArray(editableDataFromRedux) && editableDataFromRedux.length === 0;
      const isLocalAlreadyEmpty = Array.isArray(localEditableData) && localEditableData.length === 0;
      
      if (!isReduxAlreadyEmpty || !isLocalAlreadyEmpty) {
        setLocalEditableData([]);
        // Store in workflow session state (all declarations together, not split by tabs)
        if (activeWorkflowSessionId) {
          dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
            editableData: [],
          }));
        }
        dispatch(setEditableData([])); // Keep global for backward compatibility
      }
      return;
    }
    // Helper function to get current timestamp in YYYY-MM-DDTHH:MM:SS format
    const getCurrentTimestamp = () => {
      const now = new Date();
      return now.toISOString().slice(0, 19); // YYYY-MM-DDTHH:MM:SS
    };

    const defaultFields = {
      completo: 1,
      version: 2,
      nombre_usuario_ultima_modificacion: "",
      es_plantilla: 0,
      pais_ubicacion: "ES",
      tipo_ubicacion: "B",
      localizacion: "",
      divisa: "900",
      cambio_divisa: "1,0",
      es_simplificado: 0,
      es_recapitulativo: 0,
      autorizacion_por_operacion: 0,
      estado_pdc: 0,
      e_mail: "",
      contacto_telefono: "",
      divisa_iso: "EUR",
      fecha_creacion: getCurrentTimestamp(), // Always set to current time when created
      // Missing fields from PDF analysis - default values
      mrn: null, // Movement Reference Number
      fecha_operacion: null, // Operation Date
      clave_declarante: null, // Declarant Key
      lugar_entrega: null, // Delivery Place
      codigo_un: null, // UN Code for delivery place
      tipo_autorizacion_cd: null, // Type under 49 Autorización C.D.
      identificacion_adicional: null, // Additional Identification
      medio_transporte_frontera: null, // Transport Means
      documentos_transporte: [], // Transport Documents (N730, etc.)
      referencias_adicionales: [], // Additional References (Y024, etc.)
      informacion_adicional: [], // Additional Information (I5007, etc.)
      flag_datos_seguridad: 0, // Security Data Flag
    };
    let newData = [];
    if (Array.isArray(dataToUse)) {
      // Filter out null/undefined/empty objects and validate structure
      const validDeclarations = dataToUse.filter(d => 
        d !== null && 
        d !== undefined && 
        typeof d === 'object' && 
        Object.keys(d).length > 0
      );
      
      if (validDeclarations.length > 0) {
        newData = validDeclarations.map(declaration => {
          // Merge with defaults
          const merged = {
            ...defaultFields,
            ...declaration,
          };
          // Always replace fecha_creacion with current time when data is first loaded This replaces any existing value from extracted data with current time
          merged.fecha_creacion = getCurrentTimestamp();
          return merged;
        });
        setLocalEditableData(newData);
        dispatch(setEditableData(newData));
        // Update ref to keep it in sync
        editableDataRef.current = newData;
        fechaCreacionInitializedRef.current = true; // Mark as initialized
      } else {
        setLocalEditableData([]);
        // Store in workflow session state (all declarations together, not split by tabs)
        if (activeWorkflowSessionId) {
          dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
            editableData: [],
          }));
        }
        dispatch(setEditableData([])); // Keep global for backward compatibility
        editableDataRef.current = [];
      }
    } else if (dataToUse && typeof dataToUse === 'object' && Object.keys(dataToUse).length > 0) {
      const merged = {
        ...defaultFields,
        ...dataToUse,
      };
      // Always replace fecha_creacion with current time when data is first loaded This replaces any existing value from extracted data with current time
      merged.fecha_creacion = getCurrentTimestamp();
      newData = [merged];
      setLocalEditableData(newData);
      dispatch(setEditableData(newData));
      // Update ref to keep it in sync
      editableDataRef.current = newData;
      fechaCreacionInitializedRef.current = true; // Mark as initialized
    } else {
      console.warn('dataToUse is not a valid object or array');
      setLocalEditableData([]);
      // Store in workflow session state (all declarations together, not split by tabs)
      if (activeWorkflowSessionId) {
        dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
          editableData: [],
        }));
      }
      dispatch(setEditableData([])); // Keep global for backward compatibility
      editableDataRef.current = [];
    }
    
    // CRITICAL: originalDataRef management: 1. Set on initial load (if empty) - truly original data 2. Updated in handleChange BEFORE first edit - captures state before edits 3. Updated in saveChanges - becomes the new saved state DO NOT update here when Redux data changes - might have unsaved changes
    if (newData.length > 0 && !originalDataRef.current) {
      // Only set if empty (first time loading) - this is the truly original data
      originalDataRef.current = deepClone(newData);
    }
    
    // CRITICAL: Trigger validation when data is first loaded or changes This ensures validation runs even when tab data is loaded from sessionStorage
    if (newData.length > 0 && triggerValidationRef.current) {
      // Small delay to ensure refs are set up
      setTimeout(() => {
        if (triggerValidationRef.current) {
          triggerValidationRef.current();
        }
      }, 100);
    }
  }, [jsonData, extractedData, editableDataFromRedux, dispatch]);

  // Sync local isSpanish with Redux when validation errors exist
  useEffect(() => {
    if (validationErrors && validationErrors.length > 0 && isSpanishFromRedux !== undefined) {
      setIsSpanish(isSpanishFromRedux);
    }
  }, [isSpanishFromRedux, validationErrors]);

  // Validation timer ref for debouncing validation
  const validationTimerRef = useRef(null);
  // Ref to track workflow ID when validation starts (to prevent stale validations after workflow switch)
  const validationWorkflowIdRef = useRef(null);
  // Ref to store validateRequiredFields function to avoid initialization order issues
  const validateRequiredFieldsRef = useRef(null);
  // Ref to store triggerValidation function
  const triggerValidationRef = useRef(null);
  // Ref to store activeTabId to avoid closure issues
  const activeTabIdRef = useRef(activeTabId);
  // Ref for debouncing validateFieldImmediately
  const validateFieldImmediatelyDebounceRef = useRef(null);
  
  // Update activeTabId ref when it changes
  useEffect(() => {
    activeTabIdRef.current = activeTabId;
    
    // When tab switches, update editableDataRef immediately with new tab's data (ensures validation uses correct data)
    const tabsState = store.getState().tabs;
    if (activeTabId && tabsState?.tabs?.[activeTabId]?.editableData) {
      const tabEditableData = tabsState.tabs[activeTabId].editableData;
      // Normalize to array format
      const normalizedData = Array.isArray(tabEditableData) 
        ? tabEditableData 
        : (tabEditableData && typeof tabEditableData === 'object' ? [tabEditableData] : []);
      editableDataRef.current = normalizedData;
      
      // Trigger validation for the new tab's data
      if (triggerValidationRef.current && normalizedData.length > 0) {
        // Small delay to ensure state has updated
        setTimeout(() => {
          triggerValidationRef.current();
        }, 50);
      }
    } else if (!activeTabId) {
      // "All" view - use global extractedData
      const globalEditableData = store.getState().documents.editableData;
      if (globalEditableData) {
        const normalizedData = Array.isArray(globalEditableData) 
          ? globalEditableData 
          : (globalEditableData && typeof globalEditableData === 'object' ? [globalEditableData] : []);
        editableDataRef.current = normalizedData;
      }
    }
  }, [activeTabId, store]);
  
  // Helper function to trigger validation (debounced) OPTIMIZED: Immediate field validation with debouncing and hash comparison to prevent excessive updates
  const validateFieldImmediately = useCallback((declarationIndex, field, value, itemIndex = null, docIndex = null, embalajeIndex = null, marcaIndex = null) => {
    // Clear existing debounce timer
    if (validateFieldImmediatelyDebounceRef.current) {
      clearTimeout(validateFieldImmediatelyDebounceRef.current);
    }
    
    // Debounce immediate validation (150ms) to prevent excessive updates during typing
    validateFieldImmediatelyDebounceRef.current = setTimeout(() => {
      // CRITICAL: Get errors from multiple sources to ensure we have the latest
      const currentState = store.getState();
      const currentActiveWorkflowSessionId = currentState?.workflowSessions?.activeSessionId;
      
      // Priority 1: Workflow session state (source of truth)
      let currentErrors = null;
      if (currentActiveWorkflowSessionId) {
        const workflowSession = currentState?.workflowSessions?.sessions?.[currentActiveWorkflowSessionId];
        currentErrors = workflowSession?.state?.validationErrors;
      }
      
      // Priority 2: Ref (might be more up-to-date)
      if (!currentErrors?.length && validationErrorsRef.current?.length) {
        currentErrors = validationErrorsRef.current;
      }
      
      // Priority 3: Tab state (fallback)
      if (!currentErrors?.length) {
        const currentActiveTabId = activeTabIdRef.current;
        if (currentActiveTabId) {
          const tabState = currentState?.tabs?.tabs?.[currentActiveTabId];
          currentErrors = tabState?.validationErrors;
        }
      }
      
      // Priority 4: Global state (fallback)
      if (!currentErrors?.length) {
        currentErrors = currentState?.validationErrors?.errors;
      }
      
      // Normalize to array
      currentErrors = Array.isArray(currentErrors) ? currentErrors : [];
      
      // Use centralized utility to add/remove error immediately
      const declarationForKind = editableDataRef.current?.[declarationIndex];
      const updatedErrors = removeFieldErrorImmediately(
        currentErrors,
        declarationIndex,
        field,
        value,
        itemIndex,
        docIndex,
        embalajeIndex,
        marcaIndex,
        isSpanish,
        getDeclarationKind(declarationForKind)
      );
      
      // Only dispatch if errors actually changed
      if (updatedErrors !== null) {
        // Create lightweight hash to compare errors
        const currentErrorsHash = createValidationErrorsHash(currentErrors);
        const updatedErrorsHash = createValidationErrorsHash(updatedErrors);
        
        // Always dispatch if hash changed (errors actually changed)
        // CRITICAL: Also dispatch if updatedErrors is empty array and currentErrors had errors
        // This ensures errors are cleared when all errors are removed
        const shouldDispatch = currentErrorsHash !== updatedErrorsHash || 
                               (Array.isArray(updatedErrors) && updatedErrors.length === 0 && currentErrors.length > 0);
        
        if (shouldDispatch) {
          const currentState = store.getState();
          const currentActiveWorkflowSessionId = currentState?.workflowSessions?.activeSessionId;
          
          // CRITICAL: Update ALL sources to ensure UI sees the change immediately
          if (currentActiveWorkflowSessionId) {
            dispatch(updateWorkflowSessionState(currentActiveWorkflowSessionId, {
              validationErrors: updatedErrors,
            }));
          }
          
          // Also update tab/global validation errors for immediate UI update
          const currentActiveTabId = activeTabIdRef.current;
          if (currentActiveTabId) {
            dispatch(updateTabValidationErrors(currentActiveTabId, updatedErrors));
          } else {
            dispatch(setValidationErrors(updatedErrors, isSpanish));
          }
          
          // CRITICAL: Update ref immediately so subsequent calls see the updated errors
          validationErrorsRef.current = updatedErrors;
        }
      }
    }, 150); // 150ms debounce for immediate validation (reduced from 200ms for faster feedback)
  }, [dispatch, store, isSpanish]);

  const triggerValidation = useCallback(() => {
    // Clear any existing validation timer
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }
    
    // CRITICAL: Capture current workflow ID when validation starts
    const currentState = store.getState();
    const currentWorkflowId = currentState?.workflowSessions?.activeSessionId;
    validationWorkflowIdRef.current = currentWorkflowId;
    
    // Debounce validation to prevent excessive calls
    validationTimerRef.current = setTimeout(() => {
      const latestState = store.getState();
      const currentActiveWorkflowSessionId = latestState?.workflowSessions?.activeSessionId;
      
      // Skip if workflow changed during debounce
      if (validationWorkflowIdRef.current !== currentActiveWorkflowSessionId) {
        validationWorkflowIdRef.current = null;
        return;
      }
      
      // Get latest data from ref
      const currentData = editableDataRef.current;
      if (!currentData || currentData.length === 0) {
        if (currentActiveWorkflowSessionId) {
          dispatch(updateWorkflowSessionState(currentActiveWorkflowSessionId, {
            validationErrors: [],
          }));
        }
        validationErrorsRef.current = [];
        validationWorkflowIdRef.current = null;
        return;
      }
      
      // Validate using ref function
      if (!validateRequiredFieldsRef.current) {
        validationWorkflowIdRef.current = null;
        return;
      }
      
      const errors = validateRequiredFieldsRef.current();
      
      // Always update errors in workflow session state
      if (currentActiveWorkflowSessionId) {
        dispatch(updateWorkflowSessionState(currentActiveWorkflowSessionId, {
          validationErrors: errors,
        }));
      }
      
      // Also update tab/global for backward compatibility
      const currentActiveTabId = activeTabIdRef.current;
      if (currentActiveTabId) {
        dispatch(updateTabValidationErrors(currentActiveTabId, errors));
      } else {
        if (errors.length > 0) {
          dispatch(setValidationErrors(errors, isSpanish));
        } else {
          dispatch(clearValidationErrors());
        }
      }
      
      validationErrorsRef.current = errors;
      validationWorkflowIdRef.current = null;
    }, 200);
  }, [isSpanish, dispatch, store]);

  // Store triggerValidation and validateFieldImmediately in refs to avoid dependency issues
  useEffect(() => {
    triggerValidationRef.current = triggerValidation;
  }, [triggerValidation]);
  
  const validateFieldImmediatelyRef = useRef(null);
  useEffect(() => {
    validateFieldImmediatelyRef.current = validateFieldImmediately;
  }, [validateFieldImmediately]);
  
  // Expose validation function globally via window for item/document/package handlers This allows immediate validation from any component without prop drilling
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__validateFieldImmediately = (declarationIndex, field, value, itemIndex = null, docIndex = null, embalajeIndex = null, marcaIndex = null) => {
        if (validateFieldImmediatelyRef.current) {
          validateFieldImmediatelyRef.current(declarationIndex, field, value, itemIndex, docIndex, embalajeIndex, marcaIndex);
        }
      };
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.__validateFieldImmediately;
      }
    };
  }, []);
    
  // Ref to track previous editableData to prevent unnecessary validations
  const previousEditableDataRef = useRef(null);
  const isValidationRunningRef = useRef(false);
  
  // Only validate on initial load - skip validation during typing to prevent flickering
  useEffect(() => {
    if (isValidationRunningRef.current) return;
    
    const normalizedEditableData = Array.isArray(editableData) 
      ? editableData 
      : (editableData && typeof editableData === 'object' && Object.keys(editableData).length > 0 ? [editableData] : []);
    
    // Skip if same reference (no change)
    if (editableDataRef.current === normalizedEditableData) return;
    
    const currentHash = createEditableDataHash(normalizedEditableData);
    const previousHash = previousEditableDataRef.current?.hash || null;
    
    // Update ref always to keep it in sync
    editableDataRef.current = normalizedEditableData;
    
    // Only validate on initial load (previousHash === null) - skip validation during typing
    if (previousHash === null && normalizedEditableData.length > 0) {
      previousEditableDataRef.current = { hash: currentHash, data: normalizedEditableData };
      
      // Only validate on initial load if no errors exist
      const currentState = store.getState();
      const currentWorkflowSessionId = currentState?.workflowSessions?.activeSessionId;
      const workflowSession = currentWorkflowSessionId ? currentState?.workflowSessions?.sessions?.[currentWorkflowSessionId] : null;
      const storedErrors = workflowSession?.state?.validationErrors;
      
      if ((!storedErrors || !Array.isArray(storedErrors) || storedErrors.length === 0) && triggerValidationRef.current) {
        triggerValidationRef.current();
      }
    } else if (currentHash !== previousHash) {
      // Hash changed (structure changed) - update hash but don't validate (handleChange will handle it)
      previousEditableDataRef.current = { hash: currentHash, data: normalizedEditableData };
    }
    
    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
    };
  }, [editableData, store]);
  
  // Cleanup validation timers on unmount
  useEffect(() => {
    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
      if (validateFieldImmediatelyDebounceRef.current) {
        clearTimeout(validateFieldImmediatelyDebounceRef.current);
      }
    };
  }, []);
  
  const handleChange = useCallback((declarationIndex, field, value) => {
    const currentEditableData = editableDataRef.current;
    const currentValue = currentEditableData[declarationIndex]?.[field];
    
    // Normalize values for comparison (handle number/string mismatches for binary fields)
    const normalizeForComparison = (val) => {
      if (val === null || val === undefined || val === '') return null;
      if (typeof val === 'string' && (val === '0' || val === '1')) return Number(val);
      if (typeof val === 'number') return val;
      return val;
    };
    
    const normalizedCurrent = normalizeForComparison(currentValue);
    const normalizedNew = normalizeForComparison(value);
    
    // Early return if no declaration exists
    if (!currentEditableData?.[declarationIndex]) {
      return;
    }
    
    // Check if values are actually the same (after normalization)
    const valuesAreSame = normalizedCurrent === normalizedNew || 
                          (normalizedCurrent === null && normalizedNew === null) ||
                          (normalizedCurrent === undefined && normalizedNew === undefined);
    
    if (valuesAreSame) {
      return;
    }
      
    try {
      // Update ref immediately for validation and UI responsiveness
      const targetDeclaration = currentEditableData[declarationIndex];
      targetDeclaration[field] = value;
      
      // Set isDirty flag (only once)
      if (!isDirtySetRef.current) {
        isDirtySetRef.current = true;
        if (activeTabId) {
          dispatch(updateTabIsDirty(activeTabId, true));
        }
        dispatch(setDeclarationDirty(true));
      }
      
      // Update Redux immediately - keep UI in sync (optimized with shallow copy)
      const immutableCopy = currentEditableData.map((decl, idx) => 
        idx === declarationIndex ? { ...decl, [field]: value } : decl
      );
      
      dispatch(setEditableData(immutableCopy));
      
      if (activeWorkflowSessionId) {
        dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
          editableData: immutableCopy,
          isDirty: true,
        }));
      }
      
      // Immediately validate the specific field being edited to remove errors
      if (validateFieldImmediatelyRef.current) {
        validateFieldImmediatelyRef.current(declarationIndex, field, value);
      }
      
      // Only trigger full validation if this field was in error state (to catch other errors)
      const wasFieldInError = validationErrorsRef.current?.some(decl => 
        decl.declarationNumber === declarationIndex + 1 &&
        (decl.declarationErrors?.some(e => e.field === `${field}_${declarationIndex + 1}`) ||
         decl.itemErrors?.some(item => item.errors?.some(e => e.field === `${field}_${declarationIndex + 1}_${item.itemIndex}`)) ||
         decl.documentErrors?.some(doc => doc.errors?.some(e => e.field.includes(field))) ||
         decl.packageErrors?.some(pkg => pkg.errors?.some(e => e.field.includes(field))))
      );
      
      // Only run full validation if this field was in error state (might have fixed it)
      if (wasFieldInError && triggerValidationRef.current) {
        triggerValidationRef.current();
      }
    } catch (error) {
      console.error('handleChange error:', error);
    }
  }, [dispatch, activeTabId, activeWorkflowSessionId]);

  // Track if isDirty has been set to avoid unnecessary Redux dispatches
  const isDirtySetRef = useRef(false);

  // Sync isDirty ref when it changes
  useEffect(() => {
    isDirtySetRef.current = isDirty;
  }, [isDirty]);

  const validateRequiredFields = useCallback(() => {
    // Use ref to get latest editableData (not Redux to avoid re-renders)
    let currentEditableData = editableDataRef.current;
    
      // Fallback to Redux state if ref is empty
    if (!currentEditableData || !Array.isArray(currentEditableData) || currentEditableData.length === 0) {
      const state = store.getState();
      const workflowSessionId = state?.workflowSessions?.activeSessionId;
      const workflowSession = workflowSessionId ? state?.workflowSessions?.sessions?.[workflowSessionId] : null;
      const workflowEditableData = workflowSession?.state?.editableData;
      
      if (workflowEditableData && Array.isArray(workflowEditableData) && workflowEditableData.length > 0) {
        currentEditableData = workflowEditableData;
      } else {
        const globalEditableData = state?.documents?.editableData;
        if (globalEditableData && Array.isArray(globalEditableData) && globalEditableData.length > 0) {
          currentEditableData = globalEditableData;
        }
      }
    }
    
    // Early return if no data to validate
    if (!currentEditableData || !Array.isArray(currentEditableData) || currentEditableData.length === 0) {
      return [];
    }

    // Use lightweight validation - only checks required fields, no deep cloning This is much faster and doesn't cause re-renders
    return validateRequiredFieldsOnly(currentEditableData, isSpanish);
  }, [isSpanish, store]);
  
  // Store validateRequiredFields in ref after it's defined (avoids initialization order issues)
  useEffect(() => {
    validateRequiredFieldsRef.current = validateRequiredFields;
  }, [validateRequiredFields]);
  
  // Store validateRequiredFields in ref after it's defined
  useEffect(() => {
    validateRequiredFieldsRef.current = validateRequiredFields;
  }, [validateRequiredFields]);

  const saveChanges = () => {
    // Get latest data from Redux (which is now always in sync with UI)
    const currentState = store.getState();
    let dataToUse = activeWorkflowSessionId 
      ? (currentState?.workflowSessions?.sessions?.[activeWorkflowSessionId]?.state?.editableData 
         || currentState?.documents?.editableData)
      : currentState?.documents?.editableData;
    
    // Fallback to ref if Redux is empty
    if (!dataToUse || (Array.isArray(dataToUse) && dataToUse.length === 0)) {
      dataToUse = editableDataRef.current;
    }
    
    if (!dataToUse || (Array.isArray(dataToUse) && dataToUse.length === 0)) {
      console.error('saveChanges: No data to save');
      return { success: false, errors: [{ message: 'No data to save' }] };
    }
    
    // Normalize to array
    dataToUse = Array.isArray(dataToUse) ? dataToUse : [dataToUse];
    
    // Create deep copy for validation and saving
    const dataToSave = deepClone(dataToUse);
    
    // Validate data structure
    if (!Array.isArray(dataToSave) || dataToSave.length === 0 || !dataToSave.every(d => d && typeof d === 'object')) {
      console.error('saveChanges: Invalid data structure');
      return { success: false, errors: [{ message: 'Invalid data structure' }] };
    }
    
    // Temporarily update ref for validation (validateRequiredFields reads from ref), then restore
    const previousRef = editableDataRef.current;
    editableDataRef.current = dataToSave;
    const errors = validateRequiredFields();
    editableDataRef.current = previousRef;
    
    // Handle validation errors
    if (errors.length > 0) {
      // CRITICAL: Update workflow session state with errors (not just tab/global)
      if (activeWorkflowSessionId) {
        dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
          validationErrors: errors,
        }));
      }
      
      // Also update tab/global for backward compatibility
      if (activeTabId) {
        dispatch(updateTabValidationErrors(activeTabId, errors));
      } else {
        dispatch(setValidationErrors(errors, isSpanish));
      }
      
      // Update ref so validateFieldImmediately can find errors
      validationErrorsRef.current = errors;
      
      return { success: false, errors };
    }
    
    try {
      // Use the validated deep copy directly (already normalized, exact data that passed validation)
      dispatch(setEditableData(dataToSave));
      dispatch(setExtractedData(dataToSave));
      
      // Update workflow session state if active
      if (activeWorkflowSessionId) {
        // CRITICAL: Force validation after save to ensure errors are up-to-date
        const errorsAfterSave = validateRequiredFields();
        const state = store.getState();
        const session = state?.workflowSessions?.sessions?.[activeWorkflowSessionId];
        const currentCollapsed = session?.state?.declarationSummaryCollapsedByIndex ?? {};
        const aiSuggestions = session?.state?.aiSuggestions ?? [];
        const exportCount = Math.max(dataToSave?.length ?? 0, errorsAfterSave?.length ?? 0, 1);
        // Declaration Summary collapse: only export-section pending (exclude participants; those drive Review Details)
        const pendingByExport = getPendingByExportExcludingParticipants(aiSuggestions);
        const nextCollapsed = { ...currentCollapsed };
        for (let idx = 0; idx < exportCount; idx++) {
          const noErrors = !hasValidationErrorsForExport(errorsAfterSave, idx);
          const noPending = (pendingByExport[idx] ?? 0) === 0;
          if (noErrors && noPending) nextCollapsed[idx] = true;
        }
        dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
          editableData: dataToSave,
          extractedData: dataToSave, // CRITICAL: Also update extractedData so TARIC JSON generation uses saved state
          validationErrors: errorsAfterSave, // Use validated errors, not empty array
          isDirty: false,
          declarationSummaryCollapsedByIndex: nextCollapsed, // Collapse exports with no errors and no pending suggestions after save
        }));
        
        // Update ref with latest errors
        validationErrorsRef.current = errorsAfterSave;
        
        // Also update tab/global
        if (activeTabId) {
          dispatch(updateTabValidationErrors(activeTabId, errorsAfterSave));
        } else {
          if (errorsAfterSave.length > 0) {
            dispatch(setValidationErrors(errorsAfterSave, isSpanish));
          } else {
            dispatch(clearValidationErrors());
          }
        }
        
        if (activeTabId) {
          dispatch(updateTabIsDirty(activeTabId, false));
        }
        dispatch(setDeclarationDirty(false));
        // When there are no errors left after save, scroll to Review Final JSON file section
        if (errorsAfterSave.length === 0) {
          setTimeout(() => {
            scroller.scrollTo('section-review-final-json', {
              duration: 100,
              offset: -110,
              smooth: 'easeInOutQuint',
              containerId: 'analysis-report',
            });
          }, 100);
        }
      } else {
        dispatch(setDeclarationDirty(false));
      }
      
      // Update refs with saved data (use same deep copy to avoid extra JSON operations)
      originalDataRef.current = dataToSave;
      editableDataRef.current = dataToSave;
      
      // Reset dirty flags
      isDirtySetRef.current = false;
      hasLoggedIsDirtyRef.current = false;
      return { success: true };
    } catch (error) {
      console.error('Error saving changes:', error);
      return { success: false, errors: [{ message: 'Error saving changes: ' + error.message }] };
    }
  };

  const cancelChanges = useCallback(() => {
    // Get original saved data from originalDataRef (source of truth - state before any edits)
    let resetData = null;
    
    if (originalDataRef.current) {
      try {
        resetData = deepClone(originalDataRef.current);
        if (!Array.isArray(resetData)) resetData = [resetData];
        
      } catch (e) {
        console.error('Error cloning originalDataRef:', e);
        return; // Can't restore without originalDataRef
      }
    } else {
      // No originalDataRef - can't restore, just clear dirty flag
      console.warn('cancelChanges: originalDataRef is not set, cannot restore');
      if (activeTabId) {
        dispatch(updateTabIsDirty(activeTabId, false));
      }
      dispatch(setDeclarationDirty(false));
      return;
    }
    
    // Create fresh copy with no shared references (breaks connections to mutated ref objects)
    const freshData = deepClone(resetData);
    const currentState = store.getState();
    const currentWorkflowSessionId = currentState?.workflowSessions?.activeSessionId;
    
    // Reset isDirty ref first to prevent useEffect from overwriting originalDataRef when Redux updates
    isDirtySetRef.current = false;
    hasLoggedIsDirtyRef.current = false;
    
    // Update ref with fresh deep copy before Redux (ensures handleChange reads correct data immediately)
    editableDataRef.current = deepClone(freshData);
    
    // CRITICAL: Run validation IMMEDIATELY on restored data
    const previousRef = editableDataRef.current;
    editableDataRef.current = freshData;
    const validationErrorsAfterCancel = validateRequiredFieldsRef.current && freshData.length > 0
      ? validateRequiredFieldsRef.current()
      : [];
    editableDataRef.current = previousRef;
    
    // Update workflow session state first (this is what the selector reads)
    const normalizedData = Array.isArray(freshData) ? freshData : [freshData];
    if (currentWorkflowSessionId) {
      dispatch(updateWorkflowSessionState(currentWorkflowSessionId, {
        editableData: normalizedData,
        // CRITICAL: Always store validation errors, even after cancel
        validationErrors: validationErrorsAfterCancel,
      }));
    }
    
    // Update Redux state (for backward compatibility)
    dispatch(setEditableData(normalizedData));
    dispatch(setExtractedData(normalizedData));
    
    // CRITICAL: Always update validation errors in tabs/global state, regardless of isDirty
    if (activeTabId) {
      dispatch(updateTabValidationErrors(activeTabId, validationErrorsAfterCancel));
    } else {
      if (validationErrorsAfterCancel.length > 0) {
        dispatch(setValidationErrors(validationErrorsAfterCancel, isSpanish));
      } else {
        dispatch(clearValidationErrors());
      }
    }
    
    // Update validation errors ref immediately
    validationErrorsRef.current = validationErrorsAfterCancel;
    
    // Update local state last to force re-render (ensures components see change even if selector hasn't updated)
    setLocalEditableData(freshData);
    
    // Clear isDirty flags
    if (activeTabId) {
      dispatch(updateTabIsDirty(activeTabId, false));
    }
    dispatch(setDeclarationDirty(false));
    
    // CRITICAL: Restore previous template selection (or clear if there was no previous template)
    try {
      const previousTemplateStr = sessionStorage.getItem('previousSelectedTemplate');
      if (previousTemplateStr !== null) {
        const previousTemplate = JSON.parse(previousTemplateStr);
        
        // Restore template to workflow session state
        if (currentWorkflowSessionId) {
          dispatch(updateWorkflowSessionState(currentWorkflowSessionId, {
            selectedTemplate: previousTemplate,
          }));
        }
        
        // Restore template to global state (for backward compatibility)
        if (previousTemplate) {
          dispatch(setSelectedTemplate(previousTemplate));
        } else {
          dispatch(clearSelectedTemplate());
        }
      } else {
        // No previous template - clear current selection
        if (currentWorkflowSessionId) {
          dispatch(updateWorkflowSessionState(currentWorkflowSessionId, {
            selectedTemplate: null,
          }));
        }
        dispatch(clearSelectedTemplate());
      }
    } catch (e) {
      console.error('Error restoring template on cancel:', e);
      // Fallback: clear template if restore fails
      if (currentWorkflowSessionId) {
        dispatch(updateWorkflowSessionState(currentWorkflowSessionId, {
          selectedTemplate: null,
        }));
      }
      dispatch(clearSelectedTemplate());
    }
    
    // Trigger validation again to ensure everything is in sync (with minimal delay) This is a safety net in case the above validation missed something
    setTimeout(() => {
      if (triggerValidationRef.current) {
        triggerValidationRef.current();
      }
    }, 10);
  }, [store, activeTabId, activeWorkflowSessionId, dispatch, isSpanish]);

  const countTotalItems = () => {
    if (Array.isArray(editableData)) {
      return editableData.reduce((total, declaration) => {
        return total + (declaration.partidas?.length || 0);
      }, 0);
    }
    return 0;
  };

  // Helper to update both local and Redux state, AND workflow session state if activeWorkflowSessionId exists
  const updateEditableData = useCallback((newData) => {
    // Normalize to array for consistency
    const normalizedData = Array.isArray(newData) 
      ? newData 
      : (newData && typeof newData === 'object' ? [newData] : []);
    
    if (normalizedData.length === 0) {
      editableDataRef.current = normalizedData;
      setLocalEditableData(normalizedData);
      dispatch(setEditableData(normalizedData));
      return;
    }
    
    // OPTIMIZED: Fast save detection - check store directly (synchronous) for latest extractedData Use hash comparison instead of expensive JSON.stringify
    const currentState = store.getState();
    const currentExtracted = currentState?.documents?.extractedData;
    const extractedHash = createEditableDataHash(currentExtracted);
    const normalizedHash = createEditableDataHash(normalizedData);
    const isSaveOperation = currentExtracted && (
      currentExtracted === normalizedData || 
      (Array.isArray(currentExtracted) && currentExtracted.length === normalizedData.length &&
       extractedHash === normalizedHash)
    );
    
    // Capture original state before first change (for cancel to work correctly)
    if (!isDirtySetRef.current && editableDataRef.current?.length > 0) {
      try {
        originalDataRef.current = deepClone(editableDataRef.current);
        isDirtySetRef.current = true;
        if (!isSaveOperation) {
          if (activeTabId) dispatch(updateTabIsDirty(activeTabId, true));
          dispatch(setDeclarationDirty(true));
        }
      } catch (e) {
        console.error('Error capturing original state:', e);
      }
    }
    
    // If save operation, update originalDataRef to saved state
    if (isSaveOperation) {
      try {
        originalDataRef.current = deepClone(normalizedData);
        isDirtySetRef.current = false;
        hasLoggedIsDirtyRef.current = false;
        if (activeTabId) dispatch(updateTabIsDirty(activeTabId, false));
        dispatch(setDeclarationDirty(false));
      } catch (e) {
        console.error('Error updating originalDataRef on save:', e);
      }
    }
    
    // Update ref first for immediate access (ensures saveChanges can read latest data immediately)
    editableDataRef.current = normalizedData;
    setLocalEditableData(normalizedData);
    dispatch(setEditableData(normalizedData));
    
    // Update workflow session state if active
    if (activeWorkflowSessionId) {
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
        editableData: normalizedData,
      }));
      if (triggerValidationRef.current) {
        triggerValidationRef.current();
      }
    }
  }, [activeWorkflowSessionId, activeTabId, dispatch, store]);

  return {
    isSpanish,
    editableData,
    setEditableData: updateEditableData, // Use the helper that updates both local and Redux
    isDirty, // Now comes from active tab or Redux
    setIsDirty: (value) => {
      // Update per-tab isDirty if activeTabId exists, otherwise update global
      if (activeTabId) {
        dispatch(updateTabIsDirty(activeTabId, value));
      } else {
        dispatch(setDeclarationDirty(value));
      }
    },
    validationErrors, // This now comes from active tab or Redux
    handleChange,
    saveChanges,
    cancelChanges,
    countTotalItems
  };
};